import type { z } from "zod";

import type { attachmentSchema } from "@/schemas/attachment-schema";
import {
  type catalogItemSchema,
  catalogType,
  lineItemProvidedBy,
  lineItemType,
  unitTypeGroup,
} from "@/schemas/catalog-item-schema";
import type { lineItemGroupSchema } from "@/schemas/group-item-schema";
import type { laborSchema } from "@/schemas/labor-schema";
import type {
  lineItemCreateSchema,
  lineItemSchema,
  lineItemUpdateSchema,
} from "@/schemas/line-item-schema";
import type {
  commentItemSchema,
  linkPreviewSchema,
  messageBlockSchema,
  messageItemSchema,
} from "@/schemas/message-schema";
import {
  type getNotificationCountSchema,
  type notificationCountSchema,
  notificationType,
} from "@/schemas/notification-schema";
import type {
  orderDetailSchema,
  orderStatusItemSchema,
  priorityEnum,
  serviceTypeEnum,
} from "@/schemas/order-schema";
import type { placeItemSchema, placeTypeEnum } from "@/schemas/place-schema";
import type { scopeSchema } from "@/schemas/scope-schema";
import type { timePreferenceItemSchema } from "@/schemas/time-preference-item-schema";
import type { timeProposalSchema } from "@/schemas/time-proposal-schema";
import { type userItemSchema, userRoleEnum } from "@/schemas/user-item-schema";
import type { vendorItemSchema } from "@/schemas/vendor-item-schema";
import {
  type visitItemSchema,
  visitStatusEnum,
  visitTypeEnum,
} from "@/schemas/visit-schema";

export type MessageType = z.infer<typeof messageItemSchema>;
export type CommentType = z.infer<typeof commentItemSchema>;
export type AttachmentType = z.infer<typeof attachmentSchema>;
export type LinkPreviewType = z.infer<typeof linkPreviewSchema>;
export type MessageBlockType = z.infer<typeof messageBlockSchema>;
export type UserType = z.infer<typeof userItemSchema>;
export type UserRoleType = z.infer<typeof userRoleEnum>;
export const UserRoleEnum: Record<UserRoleType, UserRoleType> =
  userRoleEnum.enum;
export type TimePreferenceItemType = z.infer<typeof timePreferenceItemSchema>;
export type VisitType = z.infer<typeof visitItemSchema>;
export type VisitStatusType = z.infer<typeof visitStatusEnum>;
export const VisitStatusEnum: Record<VisitStatusType, VisitStatusType> =
  visitStatusEnum.enum;
export type VisitTypeType = z.infer<typeof visitTypeEnum>;
export const VisitTypeTypeEnum: Record<VisitTypeType, VisitTypeType> =
  visitTypeEnum.enum;
export type OrderType = z.infer<typeof orderDetailSchema>;
export type OrderPriorityType = z.infer<typeof priorityEnum>;
export type OrderServiceType = z.infer<typeof serviceTypeEnum>;
export type OrderStatusType = z.infer<typeof orderStatusItemSchema>;
export type PlaceType = z.infer<typeof placeItemSchema>;
export type PlaceTypeType = z.infer<typeof placeTypeEnum>;
export type VendorType = z.infer<typeof vendorItemSchema>;
export type LineItemType = z.infer<typeof lineItemSchema>;
export type LineItemCreateType = z.infer<typeof lineItemCreateSchema>;
export type LineItemUpdateType = z.infer<typeof lineItemUpdateSchema>;
export type ScopeType = z.infer<typeof scopeSchema>;
export type CatalogItemType = z.infer<typeof catalogItemSchema>;
export type CatalogType = z.infer<typeof catalogType>;
export const CatalogTypeEnum: Record<CatalogType, CatalogType> =
  catalogType.enum;
export type LineItemTypeType = z.infer<typeof lineItemType>;
export const LineItemTypeEnum: Record<LineItemTypeType, LineItemTypeType> =
  lineItemType.enum;
export type UnitTypeGroup = z.infer<typeof unitTypeGroup>;
export const UnitTypeGroupEnum: Record<UnitTypeGroup, UnitTypeGroup> =
  unitTypeGroup.enum;
export type LaborType = z.infer<typeof laborSchema>;
export type LineItemGroupType = z.infer<typeof lineItemGroupSchema>;
export type LineItemProvidedBy = z.infer<typeof lineItemProvidedBy>;
export const LineItemProvidedByEnum: Record<
  LineItemProvidedBy,
  LineItemProvidedBy
> = lineItemProvidedBy.enum;
export type TimeProposalType = z.infer<typeof timeProposalSchema>;
export type NotificationType = z.infer<typeof notificationType>;
export const NotificationTypeEnum: Record<NotificationType, NotificationType> =
  notificationType.enum;
export type NotificationCountResponse = z.infer<typeof notificationCountSchema>;

export type GetNotificationCountDTO = z.infer<
  typeof getNotificationCountSchema
>;
